import {Component, ElementRef, ViewContainerRef, ViewChild, OnDestroy, HostListener, ChangeDetectorRef, DoCheck} from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, FormArray, ValidatorFn } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { LightShowTrack, LightShowTrackGroup } from '../_models/LightShowTrack';
import { LightShowType } from '../_models/LightShowType';
import { LightShowTrackService } from '../_services/track.service';
import { LightShowService, LightShowFormData, LightShowListView, LightShowServiceFile, LightShowFileType, LightShowServiceFileCollection } from '../_services/light-show.service';
import { PhotoService } from '../_services/photo.service';
import { VideoService } from '../_services/video.service';
import { S3Service } from '../_services/s3.service';
import { PaymentService } from '../_services/payment-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DialogService } from '../_services/dialog.service';
import { UserService } from '../_services/user.service';
import { LightShowDefinitionMode, LightshowPreviewComponent } from '../lightshow-preview/lightshow-preview.component';
import { LightShowTypeCodes } from '../_models/LightShowTypeCodes';
import { FormHelpers } from '../_shared/form-helpers';
import { Video } from '../_models/Video';
import { BaseCueServiceComponent } from '../_shared/base.cueservice.component';
import * as moment from 'moment';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AuthService } from '../_services/auth.service';
import { DomSanitizer } from '@angular/platform-browser';
import { mergeMap, map, takeUntil } from 'rxjs/operators';
import { Observer, Observable, forkJoin, Subject, timer, of } from 'rxjs';
import { Venue } from '../_models/Venue';
import { EngineService } from '../_services/engine.service';
import { OnDeactivate } from '../_services/can-deactivate-guard.service';
import { UPNListView, UPNService } from '../_services/upn.service';
import { TrackCreateModalComponent } from '../songs/track-library/track-create-modal.component';
import { SelfieCamListView, SelfieCamService } from '../_services/selfie-cam.service';
import { MatSelect } from '@angular/material/select';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { User } from '../_models/User';

@Component({
  templateUrl: './light-show-create.component.html',
  styleUrls: ['./light-show-create.component.css']
})

export class LightShowCreateComponent extends BaseCueServiceComponent<LightShowFormData, LightShowListView> implements OnDeactivate, OnDestroy, DoCheck {
  public videos: Video[] = [];
  public defaultAssets;
  public lightShowTracks: LightShowTrack[] = [];
  public trackGroups: LightShowTrackGroup[];
  public lightShowTypes: LightShowType[];
  public videoFile: File;
  public trackFile: File;
  public customTrack: LightShowTrack;
  public form: FormGroup;
  public imageLibrary: string[] = [];
  public showServicePeriod: boolean;
  private validSubscription = false;
  private bsModalRef: BsModalRef;
  public canProgramCustomTracks = false;
  public enableUserNetworkSync = false;
  public canEditVenues = false;
  public selectTrack = false;
  private _tempFileUrl: string;
  public validationErrorMessage = 'Please complete the required fields';
  public selectedVenue: Venue;
  public saveLibTrackChanges = false;
  public upns: UPNListView[] = [];
  public selfieCams: SelfieCamListView[] = [];
  public copyUrlPrompt: string = 'Copy Lightshow File URL to Clipboard';
  public fileDownloadUrl: string = '';
  public serviceFiles: LightShowServiceFileCollection = new LightShowServiceFileCollection();
  public loadImageFiles$ = new Subject<void>();
  public lightShowOwner: User;
  public lightShowOwnerHasSubscription = true;

  public _trackService: LightShowTrackService;

  private _hasSiteManagement = false;
  private _allowClick: boolean;

  private _onDestroy$: Subject<void> = new Subject();

  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  get tempFileUrl() {
    return this._tempFileUrl;
  }

  public get selectedTrack(): LightShowTrack {
    if (this.trackFile && this.customTrack && this.selectedLightShowType === LightShowTypeCodes.customTrack) {
      return this.customTrack;
    } else {
      return this.lightShowTracks.find(t => t.trackId === this.form?.controls.trackId.value);
    }
  }

  public get selectedLightShowType(): LightShowTypeCodes {
    return this.form.controls.lightShowTypeCode.value;
  }

  @ViewChild('lightshowPreview', {static: false})
  lightshowPreview: LightshowPreviewComponent;

  @ViewChild('libraryTrackSelect')
  libraryTrackSelect: MatSelect;


  constructor(
    private trackService: LightShowTrackService,
    private videoService: VideoService,
    private fb: FormBuilder,
    private s3Service: S3Service,
    public viewRef: ViewContainerRef,
    public lightShowService: LightShowService,
    dialogService: DialogService,
    userService: UserService,
    photoService: PhotoService,
    router: Router,
    route: ActivatedRoute,
    spinner: NgxSpinnerService,
    paymentService: PaymentService,
    private modalService: BsModalService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private engineService: EngineService,
    private upnService: UPNService,
    private selfieCamService: SelfieCamService,
    public dbService: NgxIndexedDBService
  ) {
    super(lightShowService, photoService, router, route, spinner, paymentService, dialogService, viewRef, userService, dbService);
  }

  ngDoCheck(): void {

    if (this.lightshowPreview) {

      this.loadImageFiles$.next();
      this.loadImageFiles$.complete();
    }
  }

  async showDataLossDialog(event: any) {

    if (this.selectedTrack && !this._allowClick) {

      event.preventDefault();

      var path = event.path || (event.composedPath && event.composedPath());

      if (path.some(t => t?.id === "type")) {

        const result = await this.dialogService.confirm('Unsaved Changes',
          'Are you sure you want to continue? Changing Light Show type will result in any unsaved changes to be lost!',
          this.viewRef);

        if (result) {

          this._allowClick = true;
        }
      }

      if (path.some(t => t?.id === "track")) {

        const result = await this.dialogService.confirm('Unsaved Changes',
          'Are you sure you want to continue? Changing Light Show track will result in any unsaved torch data to be lost, however screen data will be kept.',
          this.viewRef);

        if (result) {

          this._allowClick = true;

        } else {

          this.libraryTrackSelect.close();
        }
      }
    }
  }

  protected getInitialDataForNewItem(): Observable<LightShowFormData> {
    return new Observable((observer: Observer<LightShowFormData>) => {
      const initialData = <LightShowFormData>{
        name: 'New LightShow',
        startDate: new Date().toString(),
        endDate: moment()
          .add(30, 'days')
          .toString(),
        couponId: null,
        useAudible: false,
        listOfEmails: [],
        redirectType: 0,
        selfieCamId: null
      };

      this.lightShowService.getSavedLightShow()
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(async savedLightShow => {
          if (savedLightShow) {
            this.spinner.hide();

            const result = await this.dialogService.restorePrompt(
              'Restore Saved Light Show?',
              'We\'ve recovered your most recent saved light show from our database, would you like to pick up where you left off?',
              this.viewRef
            );

            if (result) {
              observer.next(JSON.parse(savedLightShow.lightShowFormJSON) as LightShowFormData);
            } else {
              this.lightShowService.deleteSavedLightShow().pipe(takeUntil(this._onDestroy$)).subscribe();
              observer.next(initialData);
            }
          } else {
            observer.next(initialData);
          }

          observer.complete();
        }, err => {
          observer.next(initialData);
          observer.complete();
        });
    });
  }

  protected loadLookupData(): Observable<any> {

    return forkJoin(
      {
      tracks: this.trackService.getLightShowTracks(),
      videos: this.videoService.getVideos(),
      types: this.lightShowService.getLightShowTypes(),
      siteManagement: this.userService.hasSinglePermission('SiteManagement'),
      user: this.authService.getCurrentUser(),
      venueEdit: this.engineService.hasServiceType('VenueEdit'),
      upns: this.upnService.getAll(),
      selfieCams: this.selfieCamService.getAll(),
      isOpen: this.id > 0 ? this.lightShowService.setIsOpen(this.id, true) : of(null),
    })
    .pipe(map(data => {
      this.lightShowTracks = data.tracks;
      this.trackGroups = this.createTrackGroups(this.lightShowTracks, null);
      this.videos = data.videos;
      this.lightShowTypes = data.types.filter(t => t.code != "2B");
      this._hasSiteManagement = data.siteManagement;
      this.canProgramCustomTracks = data.user.canProgramCustomTracks;
      this.enableUserNetworkSync = data.user.enableNetworkSync;
      this.canEditVenues = data.user.canEditVenues && data.venueEdit;
      this.upns = data.upns;
      this.selfieCams = data.selfieCams;
    }));
  }

  public selectNewTrack() {
    this.selectTrack = true;
  }

  redirectChange(event) {
    if (+this.form.get('redirectType').value !== 1) {

      this.form.controls.couponId.setErrors(null);
      this.form.controls.couponId.patchValue(0);
      this.form.controls.couponId.updateValueAndValidity();
    }

    if (+this.form.get('redirectType').value !== 2) {

      this.form.controls.redirectUrl.setValidators(null);
      this.form.controls.redirectUrl.setErrors(null);
      this.form.controls.redirectUrl.patchValue(null);
      this.form.controls.redirectUrl.updateValueAndValidity();
    }

    if (+this.form.get('redirectType').value !== 3) {

      (this.form.controls.assetData as FormGroup).controls.upnId.setValidators(null);
      (this.form.controls.assetData as FormGroup).controls.upnId.setErrors(null);
      (this.form.controls.assetData as FormGroup).controls.upnId.patchValue(0);
      (this.form.controls.assetData as FormGroup).controls.upnId.updateValueAndValidity();
    }

    if (+this.form.get('redirectType').value === 3) {

      (this.form.controls.assetData as FormGroup).controls.upnId.setValidators([Validators.required, Validators.min(1)]);
      (this.form.controls.assetData as FormGroup).controls.upnId.updateValueAndValidity();
    }

    if (+this.form.get('redirectType').value !== 4){
      this.form.controls.numberOfWinners.setValidators(null);
      this.form.controls.numberOfWinners.patchValue(0);
      this.form.controls.numberOfWinners.updateValueAndValidity();
      this.form.controls.winnerMessage.setValidators(null);
      this.form.controls.winnerMessage.patchValue(null);
      this.form.controls.winnerMessage.updateValueAndValidity();
    }

    if (+this.form.get('redirectType').value === 4) {

      this.form.controls.numberOfWinners.setValidators([Validators.required, Validators.min(1)]);
      this.form.controls.numberOfWinners.patchValue(1);
      this.form.controls.winnerMessage.setValidators([Validators.required, Validators.minLength(1), Validators.maxLength(150)]);
    }

    if (+this.form.get('redirectType').value === 5) {

      this.form.controls.smsReceivingPhoneNumber.setValidators([Validators.required, Validators.pattern(/^(\+?[0-9]{11})$/)]);
      this.form.controls.smsMessage.setValidators([Validators.required]);

      this.form.controls.alertTitle.setValidators([Validators.required, Validators.maxLength(45)]);
      this.form.controls.alertBody.setValidators([Validators.required, Validators.maxLength(150)]);
      this.form.controls.alertButtonTitle.setValidators([Validators.required, Validators.maxLength(45)]);
    }

    if (+this.form.get('redirectType').value !== 5) {

      this.form.controls.smsReceivingPhoneNumber.setValidators(null);
      this.form.controls.smsReceivingPhoneNumber.reset();
      this.form.controls.smsMessage.setValidators(null);
      this.form.controls.smsMessage.reset();

      this.form.controls.alertTitle.setValidators(null);
      this.form.controls.alertTitle.reset();
      this.form.controls.alertBody.setValidators(null);
      this.form.controls.alertBody.reset();
      this.form.controls.alertButtonTitle.setValidators(null);
      this.form.controls.alertButtonTitle.reset();
    }

    if (+this.form.get('redirectType').value === 6) {

      this.form.controls.selfieCamId.setValidators([Validators.required, Validators.min(1)]);
    }

    if (+this.form.get('redirectType').value !== 6) {

      this.form.controls.selfieCamId.setValidators(null);
      this.form.controls.selfieCamId.reset();
    }

    this.form.controls.alertButtonTitle.patchValue(null);
    this.form.controls.alertTitle.patchValue(null);
    this.form.controls.alertBody.patchValue(null);

    this.form.controls.alertButtonTitle.updateValueAndValidity();
    this.form.controls.alertTitle.updateValueAndValidity();
    this.form.controls.alertBody.updateValueAndValidity();
}

  protected setFormData(data: LightShowFormData, userSubscription: any, savedData?: LightShowFormData): FormGroup {

    if (data?.dateCreated && savedData?.dateCreated) {

      data = data.dateCreated > savedData.dateCreated ? data : savedData;
    }

    this.lightShowOwner = data.user;
    this.lightShowOwnerHasSubscription = !!this.lightShowOwner?.subscriptionStartDate && !!this.lightShowOwner?.subscriptionEndDate;

    const track = this.lightShowTracks.find(t => t.trackId === data.trackId);
    const video = this.videos.find(v => v.videoId === data.videoId);
    this.fileDownloadUrl = data.fileDownloadUrl;

    this.defaultAssets = {
      cropEndPercent: 0,
      upnId: 0,
      mode: LightShowDefinitionMode.Custom,
      customModeData: {
        selectedSegmentIndex: null,
        segments: [],
        imageLibrary: [],
        colorsLibrary: []
      },
      lightShowJson: '[]'
    };

    const assets = data.assetData
      ? typeof data.assetData === 'string'
        ? JSON.parse(data.assetData)
        : data.assetData
      : this.defaultAssets;

    if (assets.standardModeData) {
      const standartModeData = assets.standardModeData;
      assets.customModeData.imageLibrary = [{
        name: standartModeData.image1Name,
        url: standartModeData.image1,
        imageBackgroundColor: '#000000',
        location: Math.random().toString(36).slice(-5)
      },
      {
        name: standartModeData.image2Name,
        url: standartModeData.image2,
        imageBackgroundColor: '#000000',
        location: Math.random().toString(36).slice(-5)
      }];

      assets.customModeData.colorsLibrary = [{ type: 'smart', colors: [standartModeData.color1, standartModeData.color2] }];
      assets.customModeData.segments = standartModeData.segments;
    }

    const form = this.fb.group({
      lightShowName: [data.name, Validators.required],
      lightShowTypeCode: [data.typeCode, Validators.required],
      period: { startDate: new Date(data.startDate), endDate: new Date(data.endDate), isNew: this.id === null },
      trackId: [data.trackId, this.requiredIfTypeIn([LightShowTypeCodes.libraryTrack, LightShowTypeCodes.videoWithLibraryTrack])],
      trackFilter: [''],
      videoId: [data.videoId, this.requiredIfTypeIn([LightShowTypeCodes.video, LightShowTypeCodes.videoWithLibraryTrack])],
      couponId: [data.couponId],
      redirectUrl: [data.redirectUrl],
      smsReceivingPhoneNumber: [""],
      smsMessage: [""],
      alertTitle: [data.alertTitle],
      alertBody: [data.alertBody],
      alertButtonTitle: [data.alertButtonTitle],
      hasSelfieCam: [data.hasSelfieCam],
      regenerateAudio: [false],
      selfieCamId: [data?.selfieCamId || 0],
      assetData: this.fb.group({
        cropEndPercent: assets.cropEndPercent,
        upnId: assets.upnId || 0,
        mode: assets.mode,
        customModeData: this.fb.group({
          selectedSegmentIndex: assets.customModeData.selectedSegmentIndex || 0,
          segments: this.fb.array(assets.customModeData.segments.map(s => LightshowPreviewComponent.segmentToGroup(this.fb, s))),
          imageLibrary: this.fb.array(assets.customModeData.imageLibrary ? assets.customModeData.imageLibrary : []),
          colorsLibrary: this.fb.array(assets.customModeData.colorsLibrary ? assets.customModeData.colorsLibrary : []),
        }),
        lightShowJson: assets.lightShowJson
      }),
      couponCode: '',
      // helper controls to enforce validation rules for videos and custom track uploads
      uploadedTrackName: [track ? track.title + '.mp3' : null, this.trackAndVideoValidator('^.*.mp3$', [LightShowTypeCodes.customTrack])],
      uploadedVideoName: [
        video ? video.title + '.mp4' : null,
        this.trackAndVideoValidator('^.*\\.mp4$', [LightShowTypeCodes.video, LightShowTypeCodes.videoWithLibraryTrack])
      ],
      insertionPattern: [data.insertionPattern, Validators.pattern(/^\s*((\d)+)\s*(\s*,\s*((\d)+))*\s*$/)],
      useAudible: [data.useAudible],
      venueId: [data.venueId],
      delayDistance: [data.delayDistance, [Validators.min(0), Validators.max(1000), Validators.pattern('[0-9]*')]],
      numberOfWinners:[data.numberOfWinners],
      winnerMessage: [data.winnerMessage],
      redirectType: [data.redirectType || 0],
      listOfEmails: [data.listOfEmails?.map(e => ({label: e})), [
        ((): ValidatorFn => {
          return (control: AbstractControl): { [key: string]: any } | null => {
            let isValid = true;
            if (control.value !== null) {
              control.value.forEach(em => {
                if (!this.isEmailValid(em.email || em.label)) {
                  isValid = false;
                }
              });
            }
            return isValid
              ? null
              : { 'email': { value: control.value } };
          };
        })()
        ]
      ]
    });

    form.controls.lightShowTypeCode.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this._allowClick = false;
        this.resetLightShowTypeFields();
        // update validity for lighshow assets data
        FormHelpers.processControlRecursively(form.get('assetData'), c => c.updateValueAndValidity());
      });

      form.controls.trackId.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {

        this._allowClick = false;
      });

    form.controls.trackId.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(trackId => {
      if (trackId) {

        this.lightShowService.trackIdManualChange$.next();
      }
    });

    if (form.controls.redirectType.value === 5) {

      form.controls.smsReceivingPhoneNumber.setValidators([Validators.required, Validators.pattern(/^(\+?[0-9]{11})$/)]);
      form.controls.smsMessage.setValidators([Validators.required]);
      form.controls.alertTitle.setValidators([Validators.required, Validators.maxLength(45)]);
      form.controls.alertBody.setValidators([Validators.required, Validators.maxLength(150)]);
      form.controls.alertButtonTitle.setValidators([Validators.required, Validators.maxLength(45)]);
      this.parseSmsUrl(form);

    } else if (form.controls.redirectType.value === 2) {

      form.controls.redirectUrl.setValidators(this.validateRedirectUrL());

    } else if (form.controls.redirectType.value === 3) {

      (form.controls.assetData as FormGroup).controls.upnId.setValidators([Validators.required, Validators.min(1)]);
      (form.controls.assetData as FormGroup).controls.upnId.updateValueAndValidity();

    } else if (form.controls.redirectType.value === 4) {

      form.controls.numberOfWinners.setValidators([Validators.required, Validators.min(1)]);
      form.controls.winnerMessage.setValidators([Validators.required, Validators.minLength(1), Validators.maxLength(150)]);

    } else if (form.controls.redirectType.value === 6) {

      form.controls.selfieCamId.setValidators([Validators.required, Validators.min(1)]);
      form.controls.selfieCamId.updateValueAndValidity();
    }

    if (userSubscription.subscriptionStartDate && userSubscription.subscriptionEndDate) {

      form.controls.period.patchValue({
        startDate: new Date(userSubscription.subscriptionStartDate),
        endDate: new Date(userSubscription.subscriptionEndDate),
        isNew: false
      });

      form.controls.period.disable();
      this.showServicePeriod = false;

    } else {

      this.showServicePeriod = true;
      this.validSubscription = true;

      if (this.lightShowOwnerHasSubscription) {

        form.controls.period.disable();
      }
    }

    return form;
  }

  private validateRedirectUrL(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let isValid = true;
      if ((control.value as string)?.includes('sms') &&
          !this.isSmsUrlValid(control.value as string)){
        isValid = false;
      }else if ((control.value as string)?.includes('http') &&
                !this.isUrlValid(control.value as string)){
          isValid = false;
      }

      return isValid
        ? null
        : { 'url': { value: control.value } };
    };
  }

  private createSmsUrl(): string{

    let phone: string = this.form.controls.smsReceivingPhoneNumber.value.toString();
    phone = phone.startsWith('+', 0) ? phone.slice(1) : phone;

    const message = encodeURIComponent(this.form.controls.smsMessage.value);

    return `sms:+${phone}?&body=${message}`;
  }

  private parseSmsUrl(form: FormGroup) {
    const url: string = form.controls.redirectUrl.value;

    if (url) {

      const phone = url.match(/([0-9]{11})/)[0];
      const message = decodeURIComponent(url.match(/\=[-_%^"!.<>{}()=\\\/'*a-zA-Z0-9]*$/)[0]);

      form.controls.smsReceivingPhoneNumber.patchValue(phone);
      form.controls.smsMessage.patchValue(message.slice(1));
    }
  }

  private requiredIfTypeIn(types: string[]): (c: AbstractControl) => ValidationErrors {

    return (c: AbstractControl): ValidationErrors => {

      if (c.parent) {

        const form = c.root as FormGroup;
        const lightShowType = form.controls.lightShowTypeCode.value;

        if (types.indexOf(lightShowType) !== -1) {

          return Validators.required(c);
        }
      }

      return null;
    };
  }

  public async onNavigatingAway() {

    if (this.id > 0) {

      this.lightShowService.setIsOpen(this.id, false).subscribe();
    }

    if ( !(await this.authService.loggedIn().toPromise()) || !this.form || !this.form.dirty) {

      return true;

    } else {

      const result = this.id
        ? await this.dialogService.confirm('Unsaved Changes', 'Are you sure you want to continue? Any unsaved changes will be lost!', this.viewRef)
        : await this.dialogService.confirmWithSaveOption('Unsaved Changes',
        'Are you sure you want to leave without saving your work? Any unsaved changes will be lost!',
        this.lightShowService.saveCurrentLightShow.bind(this.lightShowService),
        this.getFormData(this.form),
        this.viewRef);

      if (result) {

        if (this.customTrack) {

          URL.revokeObjectURL(this.customTrack.trackUrl);
        }

        return true;
      }else {
        return false
      }
    }
  }

  public saveCurrentLightShow() {
    forkJoin([this.uploadContent(), this.photoService.commitUploads()])
      .pipe(
        takeUntil(this._onDestroy$),
        mergeMap(() => {
          return this.lightShowService.saveCurrentLightShow(this.getFormData(this.form));
        })
      ).subscribe();
  }

  couponChange() {
    if (this.form.controls.hasSelfieCam.value === true) {

      this.form.controls.couponId.patchValue(0);

      this.dialogService.alert(
        'Unable to Add Coupon to Light Show',
        'The Light Show you are trying to append a coupon to already has a Selfie Cam appended to it. \
          If you would like to proceed with adding the coupon to this Light Show, please edit the Selfie Cam and remove the Light Show first.',
        this.viewRef
      );

    } else {

      this.form.controls.alertTitle.updateValueAndValidity();
      this.form.controls.alertBody.updateValueAndValidity();
    }
  }

  private trackAndVideoValidator(pattern: string, lightShowTypes: string[]): (AbstractControl) => ValidationErrors {
    return (c: AbstractControl): ValidationErrors => {

      if (c.parent) {

        const form = c.root as FormGroup;
        const lightShowType = form.controls.lightShowTypeCode.value;

        if (lightShowTypes.indexOf(lightShowType) !== -1) {

          const x = Validators.required(c) || Validators.pattern(pattern)(c);
          return x;
        }
      }

      return null;
    };
  }

  private resetLightShowTypeFields() {

    this.videoFile = null;
    this.trackFile = null;
    this.customTrack = null;
    this.form.controls.trackId.setValue(null);
    this.form.controls.videoId.setValue(null);
    this.form.controls.uploadedTrackName.setValue(null);
    this.form.controls.uploadedVideoName.setValue(null);
    this.form.controls.trackId.updateValueAndValidity();
    this.form.controls.uploadedTrackName.updateValueAndValidity();
    this.form.controls.uploadedVideoName.updateValueAndValidity();

    if (this.selectedLightShowType === LightShowTypeCodes.customTrack ||
      this.selectedLightShowType === LightShowTypeCodes.video) {

      this.form.controls.assetData.patchValue(this.defaultAssets);
    }
  }

  public videoFileChanged(files: FileList) {

    if (files.length > 0) {

      const video = files.item(0);
      this.form.patchValue({ uploadedVideoName: video.name.toLowerCase(), videoId: -1 /*-1 means pending upload*/ });
      this.form.controls.uploadedVideoName.markAsDirty();

      if (this.form.controls.uploadedVideoName.valid) {

        this.videoService.getDuration(video)
          .pipe(takeUntil(this._onDestroy$))
          .subscribe(duration => {

            if (!this.canProgramCustomTracks) {

              this.dialogService
                .alert(
                  'Please Note',
                  'Your custom CUE Light Show be ready within 7 days from upload, and only the first 3 minutes \
                    of your track will be programmed. Once complete, you cannot make changes to the audio track. If you need it sooner or need a longer track programmed, please \
                    notify your account representative for details',
                  this.viewRef
                );
            }
          });

        this.videoFile = video;

      } else {

        this.videoFile = null;
      }
    } else {

      this.videoFile = null;
    }
  }

  public trackFileChanged(files: File[]) {

    if (files.length > 0) {
      this.trackFile = files[0];

      this.serviceFiles.addTrack(this.trackFile);

      this.form.patchValue({ uploadedTrackName: this.trackFile.name.toLowerCase(), trackId: -1 /*-1 means pending upload*/ });
      this.form.controls.uploadedTrackName.markAsDirty();

      if (this.form.controls.uploadedTrackName.valid) {

        this.trackService.getDurationString(this.trackFile)
          .pipe(takeUntil(this._onDestroy$))
          .subscribe(durationString => {

            const objectUrl = URL.createObjectURL(this.trackFile);
            const mp3Url = <any>this.sanitizer.bypassSecurityTrustUrl(objectUrl);

            const customTrack: LightShowTrack = {
              trackId: -1,
              title: this.form.controls.uploadedTrackName.value,
              notes: 'Light Show custom track upload',
              artist: '',
              lightShow: '[]',
              trackUrl: mp3Url,
              sectionFlashes: '',
              duration: durationString,
              folderName: '',
              visible: false,
              genres: null,
              hasLightShow: false,
              dateCreated: null,
              createdBy: null
            };

            this.customTrack = customTrack;
          });

          if (!this.canProgramCustomTracks) {

            this.dialogService
              .alert(
                'Please Note',
                'Your custom CUE Light Show be ready within 7 days from upload, and only the first 3 minutes \
                  of your track will be programmed. Once complete, you cannot make changes to the audio track. If you need it sooner or need a longer track programmed, please \
                  notify your account representative for details',
                  this.viewRef
              );
          }

      } else {

        this.trackFile = null;
      }
    } else {

      this.trackFile = null;
    }
  }

  protected onCheckoutValidationError() {

    // select first custom segment with errors
    const customSegmentsFA = this.form.get('assetData.customModeData.segments') as FormArray;

    for (let i = 0; i < customSegmentsFA.length; i++) {

      if (!customSegmentsFA.controls[i].valid) {

        this.form.get('assetData.customModeData.selectedSegmentIndex').setValue(i);
        this.validationErrorMessage = 'One or more of your timeline segments above contains an error, this is usually caused by a poor network connection. \
           Please delete the segments highlighted in red and try adding them again. If you continue to get this message, please contact support@connectwithcue.com for assistance';

      } else {

        this.validationErrorMessage = 'Please complete the required fields';
      }
    }
  }

  selectedVenueChange(venue: Venue) {
    this.selectedVenue = venue;
  }

  protected getFormData(form: FormGroup, includeServiceFiles: boolean = false): LightShowFormData {
    if (!this.selectedTrack) { //  || !this.selectedTrack.hasLightShow
      this.form.controls.assetData.patchValue(this.defaultAssets);
    }
    // remove computed data from asset data.
    const assetData = form.get('assetData').value;
    assetData.customModeData.segments = assetData.customModeData.segments.filter(seg => seg.segmentType !== -1);

    let json = '[]';

    if (assetData.lightShowJson && assetData.lightShowJson !== '') {
      json = JSON.parse(assetData.lightShowJson);

      const newJson: any[] = [];

      Object.keys(json).forEach(k => {
        const val = json[k];
        if (!(val && val.url && val.url.changingThisBreaksApplicationSecurity)) {
          newJson.push(json[k]);
        }
      });

      assetData.lightShowJson = JSON.stringify(newJson);

      if (this.selectedVenue) {
        this.selectedVenue.venueGroups = this.selectedVenue.venueGroups?.map(g => g = g.toLowerCase().replace(/\s/g, ""));
        this.selectedVenue.venueSections?.forEach(vs => vs.groups = vs.groups?.map(g => g = g.toLowerCase().replace(/\s/g, "")));

        newJson.forEach((f, m) => {
            f.sectionList?.forEach((s, n) => {
              s = s.toLowerCase().replace(/\s/g, "");
              if (this.selectedVenue.venueGroups.indexOf(s) >= 0) {
                newJson[m].sectionList.splice(n, 1);
                const sections = this.selectedVenue.venueSections.filter(vs => vs.groups.indexOf(s) >= 0);
                newJson[m].sectionList.push(...sections.map( sn => sn.name));
              }
            });
        });
      }

      json = JSON.stringify(newJson);
    }

    // delete assetData.lightShowJson;
    return {
      couponId: form.get('couponId').value,
      name: form.get('lightShowName').value,
      json: json,
      startDate: this.formatDate(form.get('period').value.startDate),
      endDate: this.formatDate(form.get('period').value.endDate),
      trackId: form.get('trackId').value,
      assetData: JSON.stringify(assetData),
      typeCode: form.get('lightShowTypeCode').value,
      videoId: form.get('videoId').value,
      redirectUrl: +form.get('redirectType').value === 5 ? this.createSmsUrl() : form.get('redirectUrl').value,
      alertTitle: form.get('alertTitle').value,
      alertBody: form.get('alertBody').value,
      alertButtonTitle: form.get('alertButtonTitle').value,
      hasSelfieCam: form.get('hasSelfieCam').value,
      regenerateAudio: form.get('regenerateAudio').value,
      insertionPattern: form.get('insertionPattern').value,
      useAudible: form.get('useAudible').value,
      venueId: this.selectedVenue ? this.selectedVenue.venueId : null,
      delayDistance: form.get('delayDistance').value,
      winnerMessage: form.get('winnerMessage').value,
      numberOfWinners: form.get('numberOfWinners').value,
      listOfEmails: form.get('listOfEmails').value.map(item => item.label),
      selfieCamId: form.get('selfieCamId').value,
      redirectType: form.get('redirectType').value,
      dateCreated: new Date(Date.now()),
      serviceFiles: includeServiceFiles ? this.serviceFiles : null,
      user: this.lightShowOwner
    } as LightShowFormData;
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  protected uploadContent() {
    // TODO: Here we should update library track's json - create new track and change trackId and so on..

    let updateTrackJson$ = of(null);

    // Only replace the selected track's light show json with asset data when it's blank
    if (((this.selectedLightShowType === LightShowTypeCodes.customTrack && !this.selectedTrack?.hasLightShow) || this.saveLibTrackChanges)
        && this.selectedTrack.trackId !== -1) {
      this.selectedTrack.lightShow = this.form.get('assetData.lightShowJson').value;
      updateTrackJson$ = this.trackService.updateTrack(this.selectedTrack, this.saveLibTrackChanges);
    }

    const uploadVideo$ = this.videoFile
      ? this.videoService.uploadVideo(this.videoFile).pipe(map(video => this.form.patchValue({ videoId: video.videoId })))
      : of(null);

    const uploadTrack$ = this.trackFile
      ? this.trackService.uploadTrack(this.trackFile, true)
        .pipe(map(track => {
          this.form.patchValue({
            trackId: track.trackId
          }, {
            // Don't emit event here becuase we're listening to trackId with trakIdManualChange$ and it will delete segments before the lightshow builds!
            emitEvent: false
          });

          track.lightShow = this.form.get('assetData.lightShowJson').value;

          this.trackService.updateTrack(track, this.saveLibTrackChanges)
            .pipe(takeUntil(this._onDestroy$))
            .subscribe(_ => {
              if (this.customTrack) {
                URL.revokeObjectURL(this.customTrack.trackUrl);
              }
            });
        }))
      : of(null);

    return forkJoin([uploadTrack$, uploadVideo$, updateTrackJson$]);
  }

  public copyLightShowToLibTrack() {

    if (!this.selectedTrack) {
      return;
    }

    const track: LightShowTrack = this.selectedTrack;
    track.lightShow = this.form.get('assetData.lightShowJson').value;

    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    const initialState = {
      track: track,
      copyMode: true
    };

    this.bsModalRef = this.modalService
      .show(
        TrackCreateModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-lg', initialState }));
  }

  copyUrl(){
    this.copyUrlPrompt = 'Copied!';
    const countdown = timer(3000);
    countdown.subscribe(_ => {
      this.copyUrlPrompt = 'Copy Lightshow File URL to Clipboard';
    });
  }

  protected onBuildSuccess(): string {
    let baseMessage = `Your Light Show has been ${this.id ? 'updated' : 'purchased'} successfully!`;

    if ((this.selectedLightShowType === LightShowTypeCodes.customTrack || this.selectedLightShowType === LightShowTypeCodes.video) && !this.canProgramCustomTracks) {
      baseMessage = baseMessage + ' Come back and preview once CUE has a chance to program your light show! You\'ll be notified by email once your track has been completed';
    } else if (this.selectedLightShowType === LightShowTypeCodes.video && this.canProgramCustomTracks) {
      baseMessage = baseMessage + ' Now that you\'ve purchased your Video Light Show, you can come back and customize the torches whenever you\'d like! Simply click Edit on the next page';
    }

    this.lightShowService.deleteSavedLightShow()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe();

    return baseMessage;
  }

  filterLightShowTracks (search: any) {

    if (!this.lightShowTracks){

      return;
    }

    if (!search) {

      this.trackGroups = this.createTrackGroups(this.lightShowTracks, null);
      return;

    } else {

      search = search.toLowerCase();
    }

    this.trackGroups = this.createTrackGroups(this.lightShowTracks, search);
  }

  protected loadServiceFiles(data: LightShowFormData) {

    const trackFile = data.serviceFiles?.files.find(f => f.fileType === LightShowFileType.Track);

    if (trackFile) {

      this.trackFileChanged([trackFile.file]);
    }

    this.loadImageFiles$
      .subscribe(
        _ => {

          const imageFiles = data.serviceFiles.files.filter(f => f.fileType === LightShowFileType.Image);

          for (let i = 0; i < imageFiles?.length; i++) {

            this.lightshowPreview.addImageToLibrary([imageFiles[i].file], i);
          }
        }
      );
  }

  private createTrackGroups (tracks: LightShowTrack[], filter: string) : LightShowTrackGroup[] {

    const search = filter?.toLowerCase();

    const groups = tracks.reduce(
      (r, e) => {

        // Find the group this track belongs
        let group = e.title[0];

        const match = (search && e.title.toLowerCase().indexOf(search) > -1) || !search;

        // If there's no group for this character yet, we are filtering and the title matches the search term, or we aren't filtering, create a new group and add track as child
        if (!r[group] && match && e.visible) {

          r[group] = {group, children: [e]} as LightShowTrackGroup;

        // If there is a group for this character, we are filtering and the title matches the search term, or we aren't filtering, add this track to children of the group
        } else if (r[group] && match && e.visible) {

          r[group].children.push(e);
        }

        return r;
      }, {});

    return Object.values(groups).sort((a: LightShowTrackGroup, b: LightShowTrackGroup) => a.group.localeCompare(b.group)) as LightShowTrackGroup[];
  }

  private isSmsUrlValid(url: string): boolean{
    return /^sms:\+([0-9]{11})\?\&body=([%a-zA-Z0-9]*)$/.test(url)
  }

  private isUrlValid(url: string): boolean{
    return /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/.test(url);
  }

  ngOnDestroy() {

    this.saveSubscription.unsubscribe();
    this.cueService.cleanUp();

    if (this._onDestroy$) {
      this._onDestroy$.next();
      this._onDestroy$.complete();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler (event) {

    if (this.id > 0) {

      this.lightShowService.setIsOpen(this.id, false).subscribe();

      event.preventDefault();
      event.returnValue = 'Your data will be lost!';
    }
  }

  validateUrl () {
    const regex = new RegExp(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm);

    const controlValue = this.form.controls.redirectUrl.value;

    if (regex.test(controlValue) && controlValue.length > 0) {
      return
    }

    this.form.controls.redirectUrl.setErrors({invalid: true})
    this.form.controls.redirectUrl.markAsTouched();
  }
}
